import { Box, Image, ResponsiveContext, Tabs } from 'grommet';
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { Link, RouteComponentProps, match, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { LinkPrevious } from 'grommet-icons';

import { withFirebase } from '../../services/Firebase';
import { UserContext } from '../../services/Session/context';

import { HOME } from '../../constants/routes';

import Games from './Games';
import Highlights from './Highlights';
import Roster from './Roster';
import QrModal from '../../helpers/QrModal';
import Schedule from '../Schedule/Schedule';

import withLayout from '../../helpers/withLayout';
import TeamLiveCardDesktop from './LiveCard/TeamLiveCardDesktop';
import { shd } from '@shd/jslib/models';
import axios from '../../helpers/axios';
import * as Sentry from '@sentry/browser';
import GoLiveBar from '../GoLive/GoLiveBar';
import TeamsHitScope from '../HitScope/TeamsHitScope';
import { analytics } from '../../services/analytics';
import { useDebounce } from '../../helpers/useDebounce';
import { reaction } from 'mobx';
import useZendeskWidget from '../../helpers/useZendeskWidget';
import ClaimRequests from './ClaimRequests';
import { getUserTypeRelativeToTeam } from '../../helpers/utils';

import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import useTeamTrackInfo from '@hooks/useTeamTrackInfo';

import useTabs from '@hooks/useTabs';
import { useIsFeatureEnabled } from '@helpers/flagUtils';
import SHDTab from '@ui/Tab/SHDTab';
import TeamPageHeader from './TeamPageHeader';
import { TEAM_BANNER_PLACEHOLDER_URL } from '@constants/strings';
import { useMediaQuery } from '@uidotdev/usehooks';
import { MediaQuery } from '@enums/MediaQueries';
interface Props extends RouteComponentProps {
  teamId: string;
  match: match<{ handle: string }>;
  fromInvite?: string;
}
const TeamPage: React.FC<Props> = ({ teamId, history }) => {
  const teamPageHeaderRef = useRef<HTMLDivElement>(null);
  const userStore = useContext(UserContext);
  const screenSize = useContext(ResponsiveContext);

  const [showQrModal, setShowQrModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [goLiveBarOpacity, setGoLiveBarOpacity] = useState(1);

  const [teamData, setTeamData] = useState<shd.Team | null>(null);
  const { teamInfo } = useTeamTrackInfo(teamId);

  const [games, setGames] = useState<number | null>(null);
  const [players, setPlayers] = useState<number | null>(null);

  const [isScoringLink, setIsScoringLink] = useState(false);

  const teamIds = userStore.userTeams.map((userTeam) => userTeam._id);
  const isAdmin = teamIds.includes(teamId);

  const team =
    userStore.authUser && isAdmin ? userStore.selectedTeam : teamData;

  const isGameStartEnabled = useIsFeatureEnabled(
    'game_start_workflow',
    userStore
  );

  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const isLandscape = useMediaQuery(MediaQuery.IS_LANDSCAPE_MOBILE);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    if (tabParam?.toLowerCase() === 'scoring') {
      setIsScoringLink(true);
    }
  }, []);

  const { tabs, tabIndex, onActive } = useTabs(
    teamId,
    isAdmin,
    (teamData?.attrib_sportType as string) || '',
    isScoringLink
  );

  useEffect(() => {
    if (teamInfo) {
      setGames(teamInfo.gameCount);
      setPlayers(teamInfo.playerCount);
    }
  }, [teamInfo]);

  const fetchTeam = useCallback(
    async (auth = false) => {
      await axios
        .get(`/api/team/${teamId}`)
        .then((res) => {
          setTeamData(res.data.info);
          if (auth) {
            userStore.setSelectedTeam(res.data.info);
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useZendeskWidget();

  useEffect(() => {
    if (!isAdmin) {
      setLoading(true);
      fetchTeam();
    }
  }, [teamId, isAdmin, fetchTeam]);

  useEffect(() => {
    if (userStore.authUser && teamIds.includes(teamId)) {
      fetchTeam(true);
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTeam, userStore.authUser]);

  useEffect(() => {
    const handleScroll = () => {
      const startDimmingY = 200;
      const fullyDimmedY = 400;
      const fullyDimmedOpacity = 0.3;

      if (window.scrollY > startDimmingY) {
        setIsScrolled(true);
        setGoLiveBarOpacity(
          Math.max(
            fullyDimmedOpacity,
            1 -
              (window.scrollY - startDimmingY) / (fullyDimmedY - startDimmingY)
          )
        );
      } else {
        setIsScrolled(false);
        setGoLiveBarOpacity(1);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const debouncedTrackTeamPageView = useDebounce((t: shd.Team) => {
    analytics.track('Team Page View', {
      isAge13Plus: t.isAge13Plus,
      sport: t.attrib_sportType,
      userType: getUserTypeRelativeToTeam(teamId, userStore),
      teamId: t._id,
      tab: tabs ? tabs[tabIndex] : '',
    });
  }, 5000);

  useEffect(() => {
    // hack: userStore is auto observable so this component is re-rendering a lot.
    // only react to selectedTeam and aggressively debounce
    const dispose = reaction(
      () => ({ selectedTeam: userStore.selectedTeam }),
      ({ selectedTeam }) => {
        if (selectedTeam) {
          debouncedTrackTeamPageView(selectedTeam);
        }
      }
    );
    return () => dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTrackTeamPageView]);

  useEffect(() => {
    // hack: track team page view differently for logged out users
    if (team && !userStore.authUser) {
      debouncedTrackTeamPageView(team);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, debouncedTrackTeamPageView]);

  if (!team) {
    return null;
  }

  const shdContainerStyles: React.CSSProperties = {
    maxWidth: '1440px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
  };

  return (
    <Box
      id="team-page"
      fill
      background={'light-5'}
      direction="column"
      style={{
        backgroundColor: tabs[tabIndex] ? 'oklch(97.0151% 0 none)' : '#f8f8f8',
      }}
    >
      {isAdmin && team && !isGameStartEnabled && (
        <Box
          id="sticky-button"
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'fixed',
            bottom: 'env(safe-area-inset-bottom, 16px)',
            left: 0,
            right: 0,
            zIndex: 10,
            opacity: goLiveBarOpacity,
          }}
        >
          <GoLiveBar team={team} />
        </Box>
      )}
      {isAdmin && <ClaimRequests teamId={teamId} />}
      {screenSize === 'large' && !isGameStartEnabled && !isScoringLink && (
        <TeamLiveCardDesktop teamId={teamId} sport={team.attrib_sportType} />
      )}
      <Box margin={{ horizontal: 'auto 0' }}>
        <Box
          id="team-page-header"
          className={`${isScrolled ? ' is-scrolled-team-page' : 'is-not-scrolled-team-page'}`}
          background={'white'}
          ref={teamPageHeaderRef}
          pad={{
            horizontal:
              screenSize === 'medium' || screenSize === 'large'
                ? 'medium'
                : '0px',
          }}
          fill="horizontal"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
          border={{ side: 'bottom', color: 'light-1', size: '1px' }}
        >
          <Box background={'white'}>
            <Box
              skeleton={loading}
              margin={{
                horizontal: screenSize === 'medium' ? 'medium' : '0px',
              }}
            >
              {userStore.authUser && (
                <Box
                  style={{
                    ...shdContainerStyles,
                    position: 'relative',
                  }}
                >
                  <Link to={HOME}>
                    <Box
                      style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                      }}
                      alignSelf="start"
                      round="full"
                      elevation="small"
                      pad="small"
                      background="white"
                    >
                      <LinkPrevious size="24px" />
                    </Box>
                  </Link>
                </Box>
              )}

              <Box
                className={`team-page-header-image`}
                round={{
                  corner: 'bottom',
                  size: screenSize === 'small' ? '0px' : '8px',
                }}
                overflow={'hidden'}
                style={{
                  ...shdContainerStyles,
                  aspectRatio: '3/1',
                  display: isLandscape ? 'none' : 'flex',
                }}
                height={{ max: '332px' }}
              >
                <Image
                  fit="cover"
                  src={
                    teamData?.coverImage?.urlOrig || TEAM_BANNER_PLACEHOLDER_URL
                  }
                />
              </Box>

              <Box direction="row" className="nks-01">
                <Box
                  className="nks-02"
                  fill="horizontal"
                  direction="column"
                  pad={{
                    horizontal: screenSize === 'small' ? 'medium' : '0px',
                    top: 'medium',
                  }}
                  justify="between"
                  style={shdContainerStyles}
                >
                  <TeamPageHeader
                    games={games}
                    history={history}
                    isAdmin={isAdmin}
                    isScoringLink={isScoringLink}
                    newLinkingFlow={true}
                    players={players}
                    screenSize={screenSize}
                    setShowQrModal={setShowQrModal}
                    team={team}
                    teamData={teamData}
                  />
                </Box>
              </Box>
              <Box
                id="tab-navigation"
                background={'white'}
                fill="horizontal"
                style={shdContainerStyles}
              >
                <SHDThemeOverride full={false}>
                  <Box align="start">
                    <Tabs activeIndex={tabIndex} onActive={onActive}>
                      {!isScoringLink &&
                        tabs.map((tab) => <SHDTab title={tab} key={tab} />)}
                    </Tabs>
                  </Box>
                </SHDThemeOverride>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          id="team-page-content"
          width="100%"
          style={{
            ...shdContainerStyles,
            backgroundColor: 'oklch(97.0151% 0 none)',
          }}
        >
          {team && !loading && (
            <Box
              id="team-page-tab-content"
              fill="horizontal"
              pad={{
                horizontal: screenSize === 'medium' ? 'medium' : '0px',
                bottom: '88px',
              }}
            >
              {(isScoringLink ||
                (isGameStartEnabled && tabs[tabIndex] === 'Schedule')) && (
                <Schedule
                  teamId={teamId}
                  teamName={team.nameMed}
                  isIScore={team.tagger_pullerId === 'iscore-v001'}
                  isAdmin={isAdmin}
                  isScoringLink={isScoringLink}
                  hasRoster={(players && players >= 1) || false}
                  sportType={team.attrib_sportType}
                  headerHeight={
                    teamPageHeaderRef?.current?.clientHeight as number
                  }
                />
              )}
              {tabs[tabIndex] === 'Games' && (
                <Games team={team} isAdmin={isAdmin} />
              )}
              {tabs[tabIndex] === 'Roster' && (
                <Roster team={team} isAdmin={isAdmin} />
              )}

              {tabs[tabIndex] === 'HitScope' && (
                <TeamsHitScope teamIdsString={teamId} />
              )}
              {tabs[tabIndex] === 'Highlights' && !loading && (
                <Highlights team={team} isAdmin={isAdmin} />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {showQrModal && (
        <QrModal
          onClose={() => {
            setShowQrModal(false);
          }}
          team={team}
          isAdmin={isAdmin}
        />
      )}
    </Box>
  );
};

const withConditionalLayout = <P extends object>(
  Component: React.FC<P>
): React.FC<P> => {
  return (props: P) => {
    const screenSize = useContext(ResponsiveContext);
    const [LayoutComponent, setLayoutComponent] = useState<React.FC<P>>(
      () => Component
    );

    useEffect(() => {
      if (screenSize === 'small') {
        setLayoutComponent(() => Component);
      } else {
        setLayoutComponent(() => withLayout(Component));
      }
    }, [screenSize]);

    return <LayoutComponent {...props} />;
  };
};

export default compose<Props, Omit<Props, keyof RouteComponentProps>>(
  withFirebase,
  withRouter,
  withConditionalLayout
)(TeamPage);
